// Runtime redaction guard — defense-in-depth on top of the typed catalog. It drops
// any property whose KEY looks sensitive or whose VALUE is shaped like an email,
// token, certificate, XML/SAML blob, or other secret/PII. Only opaque ids, enums,
// counts, durations, booleans, and short string arrays survive.

const FORBIDDEN_KEY = /(email|mail|recipient|token|secret|password|passwd|cert|certificate|fingerprint|xml|assertion|cookie|session|csrf|authorization|bearer|ip_?addr|ipaddress|useragent|user_agent|claim|invite|saml)/i;

const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const PEM_RE = /-----BEGIN [A-Z ]+-----/;
const XML_RE = /<\?xml|<saml|<md:|<ds:Signature/i;
// Long unbroken high-entropy strings are treated as tokens/certs and rejected.
const LONG_OPAQUE_RE = /^[A-Za-z0-9_\-+/=.]{64,}$/;

function valueLooksSensitive(value: string): boolean {
  return EMAIL_RE.test(value) || PEM_RE.test(value) || XML_RE.test(value) || LONG_OPAQUE_RE.test(value);
}

export function isAllowedValue(value: unknown): value is string | number | boolean | null | string[] {
  if (value === null || typeof value === "number" || typeof value === "boolean") return true;
  if (typeof value === "string") return !valueLooksSensitive(value);
  if (Array.isArray(value)) return value.every((item) => typeof item === "string" && !valueLooksSensitive(item));
  return false;
}

/** Returns a sanitized copy with offending keys removed. Never throws. */
export function redactProps(props: Record<string, unknown>): Record<string, string | number | boolean | null | string[]> {
  const out: Record<string, string | number | boolean | null | string[]> = {};
  for (const [key, value] of Object.entries(props)) {
    if (FORBIDDEN_KEY.test(key)) continue;
    if (!isAllowedValue(value)) continue;
    out[key] = value;
  }
  return out;
}
